import { FC, Fragment, ReactNode, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { SecaoProduto } from './SecaoProduto'
import {
  mensagemErroPolitica,
  politicaProdutoService,
  statusErroPolitica,
} from '@/services/politicaProdutoService'
import {
  ComissaoDoProduto,
  ComponentePolitica,
  ORDEM_COMPONENTES,
  PoliticaVigenteProduto,
  ROTULO_COMPONENTE,
  ROTULO_MODULO,
  ROTULO_MOTIVO_AUSENCIA,
  ROTULO_NATUREZA_TRIBUTOS,
  ROTULO_OWNER,
  ROTULO_STATUS_POLITICA,
  ROTULO_STATUS_VERSAO,
  rotulo,
} from '@/types/politicaProduto'
import { formatarDataIso } from '@/components/elegibilidade/formatoElegibilidade'
import { brl, pctDeFracao } from '@/pages/simulacao/fracao'
import { formatDateTime } from '@/utils/formatters'

const COR_STATUS_POLITICA: Record<string, 'success' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  INCOMPLETE: 'warning',
  RETIRED: 'default',
}

/**
 * Chaves do `resumo` publicadas em FRAÇÃO (0,5 = 50%) — `PoliticaPublicadaEvento.Resumos` e o resumo da
 * elegibilidade. Só a unidade de exibição muda; o número é o publicado.
 */
const CHAVES_FRACAO = new Set(['ltvMaximo', 'percentual', 'percentualReceitaLiquida360', 'aliquota', 'spread'])

/** Chaves do `resumo` em reais. */
const CHAVES_BRL = new Set([
  'faturamentoAnualMinimo', 'valorOperacaoMinimo', 'valorGrandesOperacoes', 'valorAte', 'dispensaAbaixoDe',
  'toleranciaValor',
])

/** Rótulo legível das chaves conhecidas do `resumo`; chave desconhecida aparece como veio. */
const ROTULO_CHAVE: Record<string, string> = {
  tipo: 'Tipo',
  parametros: 'Parâmetros',
  ltvMaximo: 'LTV máximo',
  faturamentoAnualMinimo: 'Faturamento anual mínimo',
  valorOperacaoMinimo: 'Valor mínimo da operação',
  valorGrandesOperacoes: 'Grandes operações a partir de',
  hashRegra: 'Hash da regra',
  modelo: 'Modelo',
  modeloComissao: 'Modelo de comissão',
  recorrencia: 'Recorrência',
  eventoAtivacao: 'Evento de ativação',
  natureza: 'Natureza',
  motivoSemFormula: 'Motivo sem fórmula',
  parceiroObrigatorio: 'Parceiro obrigatório',
  mensagemIndisponibilidade: 'Mensagem de indisponibilidade',
  etapa: 'Etapa',
  schemaHash: 'Hash do schema',
  qtdCampos: 'Campos',
  conteudoHash: 'Hash do conteúdo',
  categorias: 'Categorias',
  codigo: 'Código',
  nome: 'Nome',
  itens: 'Itens',
  chave: 'Chave',
  rotulo: 'Rótulo',
  tipoDocumento: 'Tipo de documento',
  obrigatorio: 'Obrigatório',
  validadeDias: 'Validade (dias)',
  versaoFaixas: 'Versão das faixas',
  faixas: 'Faixas',
  percentualReceitaLiquida360: '% da receita líquida 360',
  ordem: 'Ordem',
  valorAte: 'Até',
  ateInclusivo: 'Teto incluso',
  percentual: 'Percentual',
  aliquota: 'Alíquota',
  base: 'Base',
  parceiroNome: 'Parceiro',
  spread: 'Spread',
}

const comoNumero = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

/** Valor folha do resumo: muda só a unidade das chaves conhecidas (fração → %, BRL); o resto como veio. */
export const valorDoResumo = (chave: string | null, v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'sim' : 'não'
  const n = chave ? comoNumero(v) : null
  if (n !== null && chave && CHAVES_FRACAO.has(chave)) return pctDeFracao(n)
  if (n !== null && chave && CHAVES_BRL.has(chave)) return brl(n)
  return String(v)
}

/**
 * Mostra um `resumo` como o servidor mandou — chave e valor, listas como listas — com rótulo legível para as chaves
 * conhecidas e a unidade de exibição das frações (%) e dos valores (R$). Não soma nem deriva nada.
 */
export const ResumoPolitica: FC<{ valor: unknown; chave?: string | null }> = ({ valor, chave = null }) => {
  if (valor === null || valor === undefined || typeof valor !== 'object') {
    return <Typography variant="body2" component="span">{valorDoResumo(chave, valor)}</Typography>
  }
  if (Array.isArray(valor)) {
    if (valor.length === 0) return <Typography variant="body2" component="span">—</Typography>
    return (
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {valor.map((item, i) => (
          <li key={i}>
            <ResumoPolitica valor={item} chave={chave} />
          </li>
        ))}
      </Box>
    )
  }
  const entradas = Object.entries(valor as Record<string, unknown>)
  if (entradas.length === 0) return <Typography variant="body2" component="span">—</Typography>
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.25 }}>
      {entradas.map(([k, v]) => (
        <Fragment key={k}>
          <Typography component="dt" variant="body2" color="text.secondary">{ROTULO_CHAVE[k] ?? k}</Typography>
          <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
            <ResumoPolitica valor={v} chave={k} />
          </Box>
        </Fragment>
      ))}
    </Box>
  )
}

/** Vigência semiaberta `[inicio, fim)`: o fim é o primeiro dia em que a versão já não vale (sem aritmética de data). */
const textoVigencia = (c: ComponentePolitica): string | null => {
  if (!c.vigenciaInicio) return null
  return c.vigenciaFim
    ? `desde ${formatarDataIso(c.vigenciaInicio)}; deixa de valer em ${formatarDataIso(c.vigenciaFim)}`
    : `desde ${formatarDataIso(c.vigenciaInicio)}`
}

const ROTULO_COMISSAO_PRODUTO: Record<keyof ComissaoDoProduto, string> = {
  modeloComissao: 'Modelo de comissão',
  recorrencia: 'Recorrência',
  eventoAtivacao: 'Evento de ativação',
}

interface CartaoProps {
  chave: string
  componente: ComponentePolitica
  consultor: boolean
  /** Linha explicativa sob o título (ex.: natureza dos tributos). */
  nota?: string | null
  /** Bloco extra do componente (ex.: o que o produto diz da comissão). */
  complemento?: ReactNode
  onVerDetalhe?: () => void
}

const CartaoComponente: FC<CartaoProps> = ({ chave, componente, consultor, nota, complemento, onVerDetalhe }) => {
  const vigencia = textoVigencia(componente)
  const titulo = componente.discriminador
    ? `${rotulo(ROTULO_COMPONENTE, chave)} — ${componente.discriminador}`
    : rotulo(ROTULO_COMPONENTE, chave)
  const ativa = componente.status === 'ATIVA' || componente.status === 'LEGADO'
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, height: '100%' }}
      data-testid={`componente-${chave}${componente.discriminador ? `-${componente.discriminador}` : ''}`}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{titulo}</Typography>
        {componente.status && (
          <Chip
            size="small"
            label={rotulo(ROTULO_STATUS_VERSAO, componente.status)}
            color={ativa ? 'success' : 'default'}
            variant={componente.status === 'ATIVA' ? 'filled' : 'outlined'}
          />
        )}
      </Stack>
      {nota && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{nota}</Typography>
      )}
      <Stack spacing={0.25} sx={{ mb: 1.5 }}>
        {componente.versao != null && (
          <Typography variant="body2">Versão {String(componente.versao)}</Typography>
        )}
        {vigencia && <Typography variant="body2">Vigência: {vigencia}</Typography>}
        {componente.owner && (
          <Typography variant="body2">Área dona: {rotulo(ROTULO_OWNER, componente.owner)}</Typography>
        )}
        {componente.origem && (
          <Typography variant="body2" color="text.secondary">Origem: {componente.origem}</Typography>
        )}
        {/* §2.4: o consultor vê só "Aprovada em"; quem propôs e aprovou é do back-office. */}
        {consultor
          ? componente.aprovadoEm && (
              <Typography variant="body2">Aprovada em {formatDateTime(componente.aprovadoEm)}</Typography>
            )
          : (componente.aprovadoPor || componente.aprovadoEm) && (
              <Typography variant="body2">
                Aprovada{componente.aprovadoPor ? ` por ${componente.aprovadoPor}` : ''}
                {componente.aprovadoEm ? ` em ${formatDateTime(componente.aprovadoEm)}` : ''}
              </Typography>
            )}
        {!consultor && componente.propostoPor && (
          <Typography variant="body2" color="text.secondary">Proposta por {componente.propostoPor}</Typography>
        )}
      </Stack>
      {componente.resumo !== undefined && componente.resumo !== null && <ResumoPolitica valor={componente.resumo} />}
      {complemento}
      {onVerDetalhe && (
        <Button size="small" sx={{ mt: 1, px: 0 }} onClick={onVerDetalhe}>
          Ver detalhe
        </Button>
      )}
    </Paper>
  )
}

interface Props {
  /** Código canônico do produto (`^[A-Z][A-Z0-9_]{2,63}$`). */
  codigo: string | null | undefined
  /** Persona consultor: sem pendentes, sem proponente/aprovador (o backend já omite; a tela reforça). */
  consultor: boolean
  /** "Ver detalhe" para a tela dona do componente; sem callback (ou consultor), o botão não aparece. */
  onVerDetalhe?: (componente: string) => void
  componentesComDetalhe?: readonly string[]
}

interface Cartao {
  id: string
  chave: string
  componente: ComponentePolitica
  nota?: string | null
  complemento?: ReactNode
}

const cartoesDe = (dados: PoliticaVigenteProduto | null): Cartao[] => {
  const c = dados?.componentes ?? {}
  const lista: Cartao[] = []
  for (const chave of ORDEM_COMPONENTES) {
    if (chave === 'formularios') {
      ;(c.formularios ?? []).forEach((f, i) =>
        lista.push({ id: `formularios-${f.discriminador ?? f.politicaId ?? i}`, chave, componente: f }),
      )
    } else if (chave === 'comissao') {
      const global = c.comissao?.global
      if (!global) continue
      const produto = c.comissao?.produto
      const entradas = produto
        ? (Object.keys(ROTULO_COMISSAO_PRODUTO) as (keyof ComissaoDoProduto)[]).filter((k) => produto[k])
        : []
      lista.push({
        id: 'comissao',
        chave,
        componente: global,
        complemento:
          produto && entradas.length > 0 ? (
            <Box sx={{ mt: 1 }} data-testid="comissao-do-produto">
              <Typography variant="caption" color="text.secondary" component="div">Do modelo do produto</Typography>
              {entradas.map((k) => (
                <Typography variant="body2" key={k}>
                  {ROTULO_COMISSAO_PRODUTO[k]}: {produto[k]}
                </Typography>
              ))}
            </Box>
          ) : undefined,
      })
    } else if (chave === 'tributos') {
      const natureza = c.tributos?.natureza ? rotulo(ROTULO_NATUREZA_TRIBUTOS, c.tributos.natureza) : null
      ;(c.tributos?.itens ?? []).forEach((t, i) =>
        lista.push({ id: `tributos-${t.discriminador ?? t.politicaId ?? i}`, chave, componente: t, nota: natureza }),
      )
    } else {
      const v = c[chave]
      if (v) lista.push({ id: chave, chave, componente: v })
    }
  }
  return lista
}

/**
 * P6 (AN-PP6 §2.1/§8) — aba "Política vigente": retrato agregado e somente leitura da política do produto na data.
 * Status, vigências, componentes, ausências e pendências vêm prontos de `GET /v1/produtos/{codigo}/politica`.
 * Política é global por produto (P-11): a mesma tela para qualquer tenant.
 */
export const PoliticaVigentePainel: FC<Props> = ({
  codigo,
  consultor,
  onVerDetalhe,
  componentesComDetalhe = [],
}) => {
  const [vigenteEm, setVigenteEm] = useState('')
  const [dados, setDados] = useState<PoliticaVigenteProduto | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)

  const carregar = useCallback(async () => {
    if (!codigo) {
      setCarregando(false)
      return
    }
    setCarregando(true)
    setErro(null)
    setSemPermissao(false)
    try {
      setDados(await politicaProdutoService.vigente(codigo, vigenteEm || null))
    } catch (e) {
      setDados(null)
      if (statusErroPolitica(e) === 403) setSemPermissao(true)
      setErro(mensagemErroPolitica(e, 'Não foi possível carregar a política vigente.'))
    } finally {
      setCarregando(false)
    }
  }, [codigo, vigenteEm])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const seletor = (
    <TextField
      type="date"
      size="small"
      label="Vigente em"
      value={vigenteEm}
      onChange={(e) => setVigenteEm(e.target.value)}
      InputLabelProps={{ shrink: true }}
      helperText={vigenteEm ? undefined : 'Vazio = hoje'}
      inputProps={{ 'aria-label': 'Vigente em' }}
    />
  )

  const descricao =
    'Retrato somente leitura da política do produto na data: cada parte continua sendo cadastrada e aprovada na tela dona. ' +
    'A mesma para todos os canais e empresas.'

  if (!codigo) {
    return (
      <SecaoProduto titulo="Política vigente" descricao={descricao}>
        <Alert severity="info">Produto sem código canônico: a política agregada é consultada pelo código.</Alert>
      </SecaoProduto>
    )
  }

  const cartoes = cartoesDe(dados)
  const ausentes = dados?.ausentes ?? []
  const pendentes = consultor ? [] : dados?.pendentesAprovacao ?? []
  const sincronizacao = Object.entries(dados?.sincronizacao ?? {}).filter(([, quando]) => !!quando)

  return (
    <SecaoProduto titulo="Política vigente" descricao={descricao} acao={seletor}>
      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="politica-vigente-carregando">
          <CircularProgress size={28} aria-label="Carregando política vigente" />
        </Box>
      ) : erro ? (
        <Alert severity={semPermissao ? 'warning' : 'error'} data-testid={semPermissao ? 'politica-sem-permissao' : 'politica-erro'}>
          {semPermissao && <strong>Sem permissão para ver a política deste produto. </strong>}
          {erro}
        </Alert>
      ) : dados ? (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={`Política ${rotulo(ROTULO_STATUS_POLITICA, dados.statusPolitica)}`}
              color={COR_STATUS_POLITICA[dados.statusPolitica] ?? 'default'}
              data-testid="status-politica"
            />
            <Typography variant="body2" color="text.secondary">
              Vigente em {formatarDataIso(dados.vigenteEm)}
            </Typography>
          </Stack>

          {/* Sem juízo de "atrasado": o backend não manda esse sinal; a tela só informa o último evento por módulo. */}
          {sincronizacao.length > 0 && (
            <Typography variant="body2" color="text.secondary" data-testid="sincronizacao">
              Última atualização recebida:{' '}
              {sincronizacao
                .map(([modulo, quando]) => `${rotulo(ROTULO_MODULO, modulo)} em ${formatDateTime(quando)}`)
                .join('; ')}
              .
            </Typography>
          )}

          {cartoes.length === 0 && ausentes.length === 0 ? (
            <Alert severity="info" data-testid="politica-vazia">
              Nenhuma política registrada para este produto nesta data.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {cartoes.map(({ id, chave, componente, nota, complemento }) => (
                <Grid item xs={12} md={6} key={id}>
                  <CartaoComponente
                    chave={chave}
                    componente={componente}
                    consultor={consultor}
                    nota={nota}
                    complemento={complemento}
                    onVerDetalhe={
                      !consultor && onVerDetalhe && componentesComDetalhe.includes(chave)
                        ? () => onVerDetalhe(chave)
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {ausentes.length > 0 && (
            <Box data-testid="politica-ausentes">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Sem versão vigente</Typography>
              <Stack spacing={0.5}>
                {ausentes.map((a) => (
                  <Typography variant="body2" key={a.componente}>
                    {rotulo(ROTULO_COMPONENTE, a.componente)}: {rotulo(ROTULO_MOTIVO_AUSENCIA, a.motivo)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {pendentes.length > 0 && (
            <Alert severity="warning" data-testid="politica-pendentes">
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Aguardando aprovação (não vale para cálculo até ser aprovada por outra pessoa):
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {pendentes.map((p) => (
                  <li key={`${p.componente}:${p.politicaId}`}>
                    <Typography variant="body2" component="span">
                      {rotulo(ROTULO_COMPONENTE, p.componente)}
                      {p.discriminador ? ` — ${p.discriminador}` : ''}
                      {p.versao != null ? ` (versão ${p.versao})` : ''}
                      {p.vigenciaInicio ? `, a partir de ${formatarDataIso(p.vigenciaInicio)}` : ''}
                      {p.propostoEm ? `, proposta em ${formatDateTime(p.propostoEm)}` : ''}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}
        </Stack>
      ) : null}
    </SecaoProduto>
  )
}

export default PoliticaVigentePainel
